/**
 * @file src/views/VID/helpers/idCaptureExitMarker.ts
 * @exports clearIdCaptureExitMarker, IdCaptureExitMarker, takeIdCaptureExitMarker, writeIdCaptureExitMarker
 * @summary Tab-scoped marker recording that the user left the page during an active ID auto-capture, so the next
 *   page load in the same tab can report that capture was lost to a reload.
 * @remarks The marker lives in `sessionStorage`, which survives a mobile OS discarding and restoring a backgrounded
 *   tab, but not the user closing the tab, so a closed tab is never counted. Only IDs, side, path and a timestamp are
 *   stored — never image or frame data. Storage access can throw (e.g. Safari private mode); every call fails silently.
 */

import type { VidIdCapturePath } from "./trackVidIdCapture";

const ID_CAPTURE_EXIT_MARKER_KEY = "vidIdCaptureExitMarker";

export interface IdCaptureExitMarker {
	captureFlowId: string;
	captureSessionId?: string;
	exitedAt: number;
	path: VidIdCapturePath;
	side: "back" | "front";
}

const isIdCaptureExitMarker = (value: unknown): value is IdCaptureExitMarker => {
	if (typeof value !== "object" || value === null) return false;
	const marker = value as Record<string, unknown>;
	return (
		typeof marker.captureFlowId === "string" &&
		(marker.captureSessionId === undefined || typeof marker.captureSessionId === "string") &&
		typeof marker.exitedAt === "number" &&
		(marker.path === "local" || marker.path === "remote") &&
		(marker.side === "back" || marker.side === "front")
	);
};

/** Records that the page was left mid-capture, replacing any earlier marker in this tab. */
export const writeIdCaptureExitMarker = (marker: IdCaptureExitMarker) => {
	try {
		window.sessionStorage.setItem(ID_CAPTURE_EXIT_MARKER_KEY, JSON.stringify(marker));
	} catch {
		// Storage unavailable — the exit just goes uncounted.
	}
};

/** Removes the marker when the capture ends or the same page comes back, so the exit is not reported. */
export const clearIdCaptureExitMarker = () => {
	try {
		window.sessionStorage.removeItem(ID_CAPTURE_EXIT_MARKER_KEY);
	} catch {
		// Storage unavailable — nothing was written either.
	}
};

/** Reads and clears the marker, so each exit is reported at most once. Malformed markers are discarded. */
export const takeIdCaptureExitMarker = (): IdCaptureExitMarker | null => {
	let raw: string | null;
	try {
		raw = window.sessionStorage.getItem(ID_CAPTURE_EXIT_MARKER_KEY);
	} catch {
		return null;
	}
	if (raw === null) return null;
	clearIdCaptureExitMarker();
	try {
		const parsed: unknown = JSON.parse(raw);
		return isIdCaptureExitMarker(parsed) ? parsed : null;
	} catch {
		return null;
	}
};
